/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable */
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import LoopIcon from '@mui/icons-material/esm/Loop';
import ClearLoop from '@mui/icons-material/esm/RemoveCircleOutline';
import DeleteAllIcon from '@mui/icons-material/esm/DeleteSweep';
import JSG from '@cedalo/jsg-ui';
import { looppath } from '@cedalo/commons';
import { graphManager } from '../../../GraphManager';
import * as Actions from '../../../actions/actions';

const styles = {
	menuItem: {
		transition: 'none',
	},
};

const getLoopPath = (treenode) => {
	const treeItem = treenode.getSelectedItem();
	return treeItem ? looppath.toString(treenode.getItemPathKeys(treeItem)) : null;
};

const getMessageBox = (controller) => controller.getParent().getParent().getModel();
const deleteTreeItem = (treeItem, state) => {
	const { controller } = state;
	if (controller) {
		const viewer = graphManager.getGraphEditor().getGraphViewer();
		const treeView = controller.getView();
		const level = treeItem == null ? -1 : treeItem.level;
		treeView.setSelection(undefined, viewer);
		const cmd = JSG.SheetCommandFactory.create('command.DeleteTreeItemCommand', treeView.getItem(), level);
		viewer.getInteractionHandler().execute(cmd);
	}
};

class TreeContextMenu extends Component {
	constructor(props) {
		super(props);
		this.state = {
			context: false,
			selected: 'false',
			selectedPath: null,
			type: '',
			top: '0px',
			left: '0px',
			anchorEl: null,
			sheetId: null
		};
	}

	componentDidMount() {
		document.addEventListener('click', this.handleClick);
		// console.log(JSG.TreeInteraction.TREE_SHOW_CONTEXT_MENU_NOTIFICATION);
		JSG.NotificationCenter.getInstance().register(
			this,
			JSG.TreeInteraction.TREE_SHOW_CONTEXT_MENU_NOTIFICATION,
			'onContextMenu'
		);

		/* eslint-disable react/no-did-mount-set-state */
		this.setState({
			context: false
		});

		/* eslint-enable react/no-did-mount-set-state */
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleClick);
		JSG.NotificationCenter.getInstance().unregister(this, JSG.TreeInteraction.TREE_SHOW_CONTEXT_MENU_NOTIFICATION);
	}

	onContextMenu(data) {
		const { controller } = data.object;
		const box = getMessageBox(controller);
		let type = box.getParent() instanceof JSG.InboxContainer ? 'ib' : 'ob';
		const sheetId = type === 'ib' ? box.getParent().getParent().properties.sheetId : null;

		type += box.getType().getValue();
		let left = data.object.event.event.offsetX;
		const maxRight = left + 285;
		if (maxRight > window.innerWidth) {
			left = window.innerWidth - 285;
		}
		const popHeight = 350;
		const maxTop = window.innerHeight - popHeight > 0 ? window.innerHeight - popHeight : 0;
		const top = data.object.event.event.offsetY > maxTop ? maxTop : data.object.event.event.offsetY;

		this.setState({
			type,
			context: true,
			controller,
			selected: controller.getModel().getSelectedItem(),
			selectedPath: getLoopPath(controller.getModel()),
			left: `${left}px`,
			top: `${top}px`,
			sheetId
		});
	}

	onDelete = () => {
		deleteTreeItem(this.state.selected, this.state);
	};

	onDeleteAll = () => {
		deleteTreeItem(undefined, this.state);
	};

	onSetLoop = () => {
		const path = this.state.selectedPath;
		const item = this.state.controller.getModel().getParent().getParent().getParent();
		item.getMessageTreeItems().getTreeItemAttributes().setActiveElement(path);
		graphManager.redraw();
		this.props.saveProcessSettings({
			machineId: graphManager.graph.machineContainer.machineId,
			streamsheetId: this.state.sheetId,
			loop: {
				path,
				enabled: true
			}
		});
	};

	onClearLoop = () => {
		const item = this.state.controller.getModel().getParent().getParent().getParent();
		item.getMessageTreeItems().getTreeItemAttributes().setActiveElement('');
		this.props.saveProcessSettings({
			machineId: graphManager.graph.machineContainer.machineId,
			streamsheetId: this.state.sheetId,
			loop: {
				path: '',
				enabled: false
			}
		});
	};

	handleClick = (event) => {
		const { context } = this.state;
		const wasOutside = !(event.target.contains === this.sheetmenu);

		if (wasOutside && context) {
			this.setState({
				context: Boolean(this.state.anchorEl) || false
			});
		}
	};

	handleClose = () => {
		this.setState({ anchorEl: null });
	};

	render() {
		if (!this.state.context) {
			return null;
		}

		return (
			<Paper
				id="sheetmenu"
				ref={(ref) => {
					this.sheetmenu = ref;
				}}
				style={{
					display: [this.state.context ? 'inline-block' : 'none'],
					position: 'absolute',
					left: [this.state.left],
					top: [this.state.top]
				}}
			>
				<MenuList>
					<MenuItem
						onClick={this.onDelete}
						dense
						disabled={this.state.selected === undefined}
						style={{
							display: [this.state.type === 'ibml' || this.state.type === 'obml' ? 'flex' : 'none']
						}}
					>
						<ListItemIcon>
							<DeleteIcon style={styles.menuItem} />
						</ListItemIcon>
						<ListItemText primary={<FormattedMessage id="ContextMenus.Delete" defaultMessage="Delete" />} />
					</MenuItem>
					<MenuItem
						onClick={this.onDeleteAll}
						dense
						style={{
							display: [this.state.type === 'ibml' || this.state.type === 'obml' ? 'flex' : 'none']
						}}
					>
						<ListItemIcon>
							<DeleteAllIcon style={styles.menuItem} />
						</ListItemIcon>
						<ListItemText
							primary={<FormattedMessage id="ContextMenus.DeleteAll" defaultMessage="Delete All" />}
						/>
					</MenuItem>
					<MenuItem
						onClick={this.onSetLoop}
						dense
						disabled={
							this.state.selected === undefined ||
							(this.state.selected.type !== JSG.TreeItemsNode.DataType.ARRAY &&
								this.state.selected.type !== JSG.TreeItemsNode.DataType.OBJECT)
						}
						style={{
							display: [this.state.type === 'ibme' ? 'flex' : 'none']
						}}
					>
						<ListItemIcon>
							<LoopIcon style={styles.menuItem} />
						</ListItemIcon>
						<ListItemText
							primary={<FormattedMessage id="ContextMenus.SetLoopElement" defaultMessage="Set Loop" />}
						/>
					</MenuItem>
					<MenuItem
						onClick={this.onClearLoop}
						dense
						style={{
							display: [this.state.type === 'ibme' ? 'flex' : 'none']
						}}
					>
						<ListItemIcon>
							<ClearLoop style={styles.menuItem} />
						</ListItemIcon>
						<ListItemText
							primary={
								<FormattedMessage id="ContextMenus.ClearLoopElement" defaultMessage="Clear Loop" />
							}
						/>
					</MenuItem>
				</MenuList>
			</Paper>
		);
	}
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export default connect(null, mapDispatchToProps)(TreeContextMenu);
